// The endpoint Test call (F3-3): what the web app's `POST /v1/applications` would get from this endpoint right now. It
// honours the single switch (lender off → 409), the product switch and an admin's suspend, runs the one workflow version
// behind the endpoint through the engine (every API node in the batch's sandbox, LH-F5) and returns the exact request
// (applicant masked), the standard answer and the run-log entry (reference ids only).
import { batchOf, effectiveState } from '@/lib/flow'
import { executeFlow, maskApplicant, newLogId, type ExecResult, type Invoke } from '@/lib/flowEngine'
import type { Batch, Lender, RunStep, ServiceEndpoint, WorkflowRun, WorkflowVersion } from '@/types'

export interface TestCallResult {
  httpStatus: number
  request: { method: 'POST'; path: '/v1/applications'; headers: Record<string, string>; body: Record<string, unknown> }
  response: unknown
  result: ExecResult | null
  run: WorkflowRun | null
}

export async function testCall(ep: ServiceEndpoint, lender: Lender, flows: WorkflowVersion[], batches: Batch[], applicant: Record<string, unknown>, replies: Record<string, string>, invoke: Invoke): Promise<TestCallResult> {
  const request: TestCallResult['request'] = {
    method: 'POST',
    path: '/v1/applications',
    headers: { 'x-app-key': '••••', 'content-type': 'application/json' },
    body: { endpoint_id: ep.id, environment: 'uat', applicant: maskApplicant(applicant), consent_receipt_ids: ['cr_demo…'], context: { source: 'test call' } },
  }
  const product = lender.products.find((p) => p.slug === ep.productSlug)
  const state = effectiveState(ep, lender, product)
  if (state === 'retired') return { httpStatus: 404, request, response: { error: 'endpoint_not_found', endpoint_id: ep.id }, result: null, run: null }
  if (state === 'lender_off' || state === 'suspended') return { httpStatus: 409, request, response: { error: 'lender_unavailable', endpoint_id: ep.id, message: `${lender.name} is not available right now.` }, result: null, run: null }
  if (state === 'product_inactive') return { httpStatus: 409, request, response: { error: 'product_unavailable', endpoint_id: ep.id }, result: null, run: null }

  const flow = flows.find((f) => f.name === ep.workflowName && f.version === ep.workflowVersion)
  const batch = flow ? batchOf(flow, batches) : undefined
  if (!flow || !batch) return { httpStatus: 500, request, response: { error: 'endpoint_misconfigured', detail: !flow ? 'workflow version missing' : 'batch missing' }, result: null, run: null }

  const logId = newLogId()
  const result = await executeFlow(flow, batch, { applicant, env: { environment: 'uat', ref: String(applicant.ref ?? 'rec_test') }, replies, lenderName: lender.name, fallbackLink: lender.links.web || null, logId, invoke })
  const base = lender.env.uatBaseUrl || ''
  const steps: RunStep[] = result.steps
    .filter((s) => s.kind !== 'start' && s.kind !== 'answer')
    .map((s) => ({
      node: s.node,
      kind: s.kind,
      ...(s.endpoint ? { call: `${s.endpoint}${s.call ? ` → ${s.call.method} ${s.call.url.replace(base, '')}` : ''}` } : {}),
      outcome: s.port,
      ...(typeof s.value?.reply_code === 'string' ? { replyCode: s.value.reply_code } : {}),
      ms: s.ms,
      ...(s.waitS ? { note: `wait ${s.waitS} s` } : {}),
    }))
  const run: WorkflowRun = {
    id: `r_${Date.now().toString(36)}`,
    at: new Date().toISOString(),
    endpointId: ep.id,
    lenderSlug: lender.slug,
    workflowVersion: flow.version,
    batchVersion: batch.version,
    env: 'uat',
    source: 'test call',
    steps,
    ms: result.ms,
    answer: result.answer.status,
    appRef: `test-${Math.random().toString(16).slice(2, 4)}…`,
    logId,
  }
  return { httpStatus: 200, request, response: result.answer, result, run }
}
